import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { ScryfallCard } from "@/types/scryfall";
import { Command, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { MTGCard } from "../MTGCard";

import {
  Sun,
  Droplet,
  Skull,
  Flame,
  Leaf,
  Crown,
  Search,
  HelpCircle,
  Dices
} from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

interface SimplifiedCommanderStepProps {
  commander: ScryfallCard | null;
  setCommander: (card: ScryfallCard | null) => void;
  setColorIdentity: (colors: string[] | ((prev: string[]) => string[])) => void;
  colorIdentity: string[];
  scryfallApiUrl: string;
}

const COLOR_OPTIONS = [
  {
    color: 'W',
    icon: <Sun className="w-6 h-6" />,
    name: 'White',
    description: 'Order, healing, protection',
    className: 'data-[state=on]:bg-yellow-200 data-[state=on]:text-yellow-900 data-[state=on]:border-yellow-400 hover:bg-yellow-50'
  },
  {
    color: 'U',
    icon: <Droplet className="w-6 h-6" />,
    name: 'Blue',
    description: 'Card draw, counterspells, control',
    className: 'data-[state=on]:bg-blue-500 data-[state=on]:text-white data-[state=on]:border-blue-600 hover:bg-blue-50'
  },
  {
    color: 'B',
    icon: <Skull className="w-6 h-6" />,
    name: 'Black',
    description: 'Removal, graveyard, sacrifice',
    className: 'data-[state=on]:bg-gray-800 data-[state=on]:text-white data-[state=on]:border-gray-900 hover:bg-gray-50'
  },
  {
    color: 'R',
    icon: <Flame className="w-6 h-6" />,
    name: 'Red',
    description: 'Damage, aggression, chaos',
    className: 'data-[state=on]:bg-red-500 data-[state=on]:text-white data-[state=on]:border-red-600 hover:bg-red-50'
  },
  {
    color: 'G',
    icon: <Leaf className="w-6 h-6" />,
    name: 'Green',
    description: 'Big creatures, ramp, nature',
    className: 'data-[state=on]:bg-green-500 data-[state=on]:text-white data-[state=on]:border-green-600 hover:bg-green-50'
  },
];

export const SimplifiedCommanderStep = ({
  commander,
  setCommander,
  setColorIdentity,
  colorIdentity,
  scryfallApiUrl
}: SimplifiedCommanderStepProps) => {
  const [commanderSearch, setCommanderSearch] = useState('');
  const [commanderSuggestions, setCommanderSuggestions] = useState<ScryfallCard[]>([]);
  const [isSearchingCommander, setIsSearchingCommander] = useState(false);
  const [isFetchingRandom, setIsFetchingRandom] = useState(false);

  const fetchRandomCommander = async () => {
    setIsFetchingRandom(true);
    try {
      const response = await fetch(`${scryfallApiUrl}/cards/random?q=is%3Acommander+game%3Apaper`, {
        referrerPolicy: 'no-referrer',
      });
      const data = await response.json();
      if (data) {
        setCommander(data);
        setColorIdentity(data.color_identity);
      }
    } catch (error) {
      console.error("Failed to fetch random commander:", error);
    } finally {
      setIsFetchingRandom(false);
    }
  };


  // Commander search effect
  useEffect(() => {
    if (commanderSearch.length < 3) {
      setCommanderSuggestions([]);
      setIsSearchingCommander(false);
      return;
    }
    setIsSearchingCommander(true);
    const fetchCommanders = async () => {
      try {
        const response = await fetch(`${scryfallApiUrl}/cards/search?q=${encodeURIComponent(commanderSearch)}+is%3Acommander+game%3Apaper`, {
          referrerPolicy: 'no-referrer',
        });
        const data = await response.json();
        setCommanderSuggestions(data.data || []);
      } catch {
        setCommanderSuggestions([]);
      } finally {
        setIsSearchingCommander(false);
      }
    };
    const debounce = setTimeout(fetchCommanders, 300);
    return () => clearTimeout(debounce);
  }, [commanderSearch, scryfallApiUrl]);

  return (
    <div className="space-y-4 md:space-y-6">
      {/* Simplified Hero Section */}
        <div className="flex items-center justify-center gap-2">
          <div className="w-12 h-12 md:w-16 md:h-16 bg-gradient-to-br from-primary/20 to-primary/30 rounded-full flex items-center justify-center shadow-lg flex-shrink-0">
            <Crown className="w-6 h-6 md:w-8 md:h-8 text-primary" />
          </div>
          <div className="text-left">
            <h2 className="text-lg md:text-xl font-bold">Choose Your Commander</h2>
            <p className="text-muted-foreground text-xs md:text-sm max-w-md">
              Your commander determines your deck's colors and strategy.
            </p>
          </div>
        </div>

      {/* Selected Commander Display */}
      {commander && (
        <div className="flex justify-center">
          <div className="relative group">
            <div className="absolute -inset-1 bg-gradient-to-r from-primary/50 to-primary/30 rounded-lg blur opacity-75"></div>
            <div className="relative bg-background rounded-lg p-3 md:p-3">
              <div className="w-40 md:w-32 mx-auto">
                <MTGCard card={commander} />
              </div>
              <div className="mt-2 text-center">
                <div className="font-medium text-sm">{commander.name}</div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setCommander(null);
                    setCommanderSearch('');
                    setColorIdentity([]);
                    setCommanderSuggestions([]);
                  }}
                  className="mt-1 text-xs h-7 px-3"
                >
                  Change
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Commander Search */}
      <div className="space-y-2">
        <div className="flex items-center gap-2 mb-1 md:mb-2">
          <Search className="w-4 h-4 text-primary" />
          <Label htmlFor="commander-search" className="font-medium text-sm md:text-base">Search for a Commander</Label>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger>
                <HelpCircle className="w-4 h-4 text-muted-foreground" />
              </TooltipTrigger>
              <TooltipContent>
                <p className="max-w-xs">Commanders are legendary creatures that lead your deck. Try searching for names like "Atraxa" or "Edgar Markov"</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
        <div className="flex items-start gap-2">
          <div className="relative flex-grow">
            <Command className="border-2 border-primary/20 rounded-lg" shouldFilter={false}>
              <CommandInput
                id="commander-search"
                placeholder="Try 'Atraxa' or 'Edgar'..."
                value={commanderSearch}
                onValueChange={setCommanderSearch}
                className="border-0 text-sm md:text-base"
              />
              {isSearchingCommander && (
                <div className="absolute right-3 top-1/2 -translate-y-1/2">
                  <div className="w-5 h-5 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
                </div>
              )}
              {(commanderSuggestions.length > 0 || (commanderSearch.length >= 3 && !isSearchingCommander)) && (
                <CommandList className="max-h-60 border-t-2 border-primary/20">
                  {commanderSuggestions.length > 0 ? (
                    commanderSuggestions.slice(0, 8).map(card => (
                      <CommandItem
                        key={card.id}
                        value={card.name}
                        onSelect={() => {
                          setCommander(card);
                          setColorIdentity(card.color_identity);
                          setCommanderSearch('');
                          setCommanderSuggestions([]);
                        }}
                        className="cursor-pointer hover:bg-primary/10"
                      >
                        <div className="flex items-center gap-2">
                          <Crown className="w-4 h-4 text-primary" />
                          <div>
                            <div className="font-medium text-sm">{card.name}</div>
                            <div className="text-xs text-muted-foreground">
                              {card.mana_cost && <span className="font-mono">{card.mana_cost}</span>}
                            </div>
                          </div>
                        </div>
                      </CommandItem>
                    ))
                  ) : (
                    <div className="p-4 text-center text-muted-foreground">
                      <div className="space-y-1">
                        <div className="text-sm">No commanders found for "{commanderSearch}"</div>
                        <div className="text-xs">Try a different name or check your spelling</div>
                      </div>
                    </div>
                  )}
                </CommandList>
              )}
            </Command>
          </div>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="outline"
                  className="h-12 w-12"
                  onClick={fetchRandomCommander}
                  disabled={isFetchingRandom}
                >
                  {isFetchingRandom ? (
                    <div className="w-5 h-5 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
                  ) : (
                    <Dices className="w-5 h-5" />
                  )}
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p>Pick a random commander</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      </div>

      {/* Simplified Color Selection */}
      <div className="space-y-3 md:space-y-4">
        <div className='relative'>
          <div className='absolute inset-0 flex items-center'>
            <div className='w-full border-t border-slate-700'></div>
          </div>
          <div className='relative flex justify-center text-sm'>
            <span className='px-3 bg-background text-slate-400'>
              Or, choose your own color identity
            </span>
          </div>
        </div>

        <div className="grid grid-cols-5 gap-2 md:gap-2">
          {COLOR_OPTIONS.map(({ color, icon, name, description, className }) => (
            <div key={color} className="space-y-2">
              <ToggleGroup
                type="multiple"
                variant="outline"
                value={colorIdentity}
                onValueChange={setColorIdentity}
                className="w-full"
              >
                <ToggleGroupItem
                  value={color}
                  aria-label={`Toggle ${name}`}
                  className={`${className} w-full h-14 md:h-14 flex-col gap-1 text-xs md:text-sm font-medium transition-all duration-200 hover:scale-105 touch-manipulation`}
                  title={name}
                >
                  <div className="w-5 h-5 md:w-6 md:h-6">
                    {icon}
                  </div>
                  <span className="text-[.8rem] pt-2 sm:p-0 leading-none">{name}</span>
                </ToggleGroupItem>
              </ToggleGroup>
              <div className="text-center px-1">
                <div className="hidden sm:block sm:text-xs text-muted-foreground leading-tight">{description}</div>
              </div>
            </div>
          ))}
        </div>

        {colorIdentity.length > 0 && (
          <div className="text-center p-2 md:p-3 bg-primary/5 rounded-lg">
            <div className="font-medium text-primary text-xs md:text-sm">
              {colorIdentity.length === 1 ? 'Mono-color' :
                colorIdentity.length === 2 ? 'Two-color' :
                colorIdentity.length === 3 ? 'Three-color' :
                colorIdentity.length === 4 ? 'Four-color' : 'Five-color'} deck selected
            </div>
            <div className="text-xs text-muted-foreground mt-1">
              Colors: {colorIdentity.join(' • ')}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};